/**
 * Allow-list for URLs the portal loads into an iframe (SafeAeon v1 EXT-002).
 *
 * `#/permits/:departmentId/details?viewDetails=<URL>` and `#/userprocessflows?path=<URL>` used to frame any http(s)
 * URL, so a crafted link could show another site's page under the state's domain. Now only this page's origin and
 * the origins the portal's `env.json` configures may be framed.
 *
 * Pure checks only. The env.json reader lives next to this file in `embedAllowedOrigins.ts`.
 * No imports on purpose: the CMS viewer's `test/embedAllowlist.test.mjs` (`yarn test`) loads this file directly in Node.
 */
/** Hosts where plain `http:` is tolerated: local development only. */
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);
/**
 * The origin (`https://host[:port]`) of a configured absolute URL, or `''` when it is not one.
 * Plain `http:` is accepted only for loopback hosts.
 */
export function toEmbedOrigin(value) {
    if (typeof value !== 'string' || !value.trim())
        return '';
    try {
        const u = new URL(value.trim());
        if (u.protocol === 'https:')
            return u.origin;
        if (u.protocol === 'http:' && LOOPBACK_HOSTS.has(u.hostname))
            return u.origin;
    }
    catch {
        /* not an absolute URL */
    }
    return '';
}
/**
 * Origins from configured URLs plus an explicit list. The list may be an array of strings or one string
 * separated by spaces or commas (`env.json` `EmbedAllowedOrigins`). Invalid entries are dropped.
 */
export function collectEmbedOrigins(configuredUrls, explicit) {
    const listed = Array.isArray(explicit)
        ? explicit
        : typeof explicit === 'string'
            ? explicit.split(/[\s,]+/)
            : [];
    const origins = new Set();
    for (const value of [...configuredUrls, ...listed]) {
        const origin = toEmbedOrigin(value);
        if (origin)
            origins.add(origin);
    }
    return origins;
}
/**
 * True when `url` may be loaded into a portal iframe: an absolute http(s) URL without credentials, whose origin is
 * `pageOrigin` or one of `allowedOrigins`. Relative, protocol-relative, `javascript:`, `data:`, `blob:` and
 * unparseable values are refused.
 */
export function isAllowedEmbedUrl(url, allowedOrigins, pageOrigin) {
    let u;
    try {
        u = new URL(url.trim());
    }
    catch {
        return false;
    }
    if (u.protocol !== 'https:' && u.protocol !== 'http:')
        return false;
    if (u.username || u.password)
        return false;
    return u.origin === pageOrigin || allowedOrigins.has(u.origin);
}
